import { Observable } from "core/Misc/observable";
import { RefreshNode } from "./tools";
import commonStyles from "./common.module.scss";
import styles from "./nodeLink.module.scss";
export class NodeLink {
    get isTargetCandidate() {
        return this._isTargetCandidate;
    }
    set isTargetCandidate(value) {
        if (this._isTargetCandidate === value) {
            return;
        }
        this._isTargetCandidate = value;
        if (value) {
            this._path.classList.add(styles["target-candidate"]);
        }
        else {
            this._path.classList.remove(styles["target-candidate"]);
        }
    }
    get isVisible() {
        return this._isVisible;
    }
    set isVisible(value) {
        this._isVisible = value;
        if (!value) {
            this._path.classList.add(commonStyles["hidden"]);
            this._selectionPath.classList.add(commonStyles["hidden"]);
        }
        else {
            this._path.classList.remove(commonStyles["hidden"]);
            this._selectionPath.classList.remove(commonStyles["hidden"]);
        }
        this.update();
    }
    get portA() {
        return this._portA;
    }
    get portB() {
        return this._portB;
    }
    get nodeA() {
        return this._nodeA;
    }
    get nodeB() {
        return this._nodeB;
    }
    intersectsWith(rect) {
        const locatRect = this._path.getBoundingClientRect();
        if (rect.left > locatRect.right || rect.right < locatRect.left || rect.top > locatRect.bottom || rect.bottom < locatRect.top) {
            return false;
        }
        const zoom = this._graphCanvas.zoom;
        const svg = this._graphCanvas.svgCanvas;
        const rootRect = svg.getBoundingClientRect();
        const left = (rect.x - rootRect.x) / zoom;
        const top = (rect.y - rootRect.y) / zoom;
        const right = left + rect.width / zoom;
        const bottom = top + rect.height / zoom;
        const sampleRate = 10; // Checking 10 times on the path should be enough
        for (let index = 0; index < 1; index += 1 / sampleRate) {
            const point = this._path.getPointAtLength(index * this._path.getTotalLength());
            if (left < point.x && right > point.x && top < point.y && bottom > point.y) {
                return true;
            }
        }
        return false;
    }
    update(endX = 0, endY = 0, straight = false) {
        const rectA = this._portA.element.getBoundingClientRect();
        const rootRect = this._graphCanvas.canvasContainer.getBoundingClientRect();
        const zoom = this._graphCanvas.zoom;
        const xOffset = rootRect.left;
        const yOffset = rootRect.top;
        const startX = (rectA.left - xOffset + 0.5 * rectA.width) / zoom;
        const startY = (rectA.top - yOffset + 0.5 * rectA.height) / zoom;
        if (this._portB) {
            const rectB = this._portB.element.getBoundingClientRect();
            endX = (rectB.left - xOffset + 0.5 * rectB.width) / zoom;
            endY = (rectB.top - yOffset + 0.5 * rectB.height) / zoom;
        }
        // We need a volume to allow gradient to work
        if (startY === endY) {
            endY += 0.01;
        }
        if (straight) {
            this._path.setAttribute("d", `M${startX},${startY} L${endX},${endY}`);
            this._path.setAttribute("stroke-dasharray", "10, 10");
            this._path.setAttribute("stroke-linecap", "round");
        }
        else {
            const deltaX = endX - startX;
            const deltaY = endY - startY;
            const tangentLength = Math.min(Math.sqrt(deltaX * deltaX + deltaY * deltaY) * 0.5, 300);
            this._path.setAttribute("d", `M${startX},${startY} C${startX + tangentLength},${startY} ${endX - tangentLength},${endY} ${endX},${endY}`);
            this._selectionPath.setAttribute("d", `M${startX},${startY} C${startX + tangentLength},${startY} ${endX - tangentLength},${endY} ${endX},${endY}`);
        }
        // No red as it means no type yet
        let extractedColorB = "";
        const extractedColorA = this._graphCanvas.stateManager.getPortColor(this._portA.portData);
        if (this._portB) {
            extractedColorB = this._graphCanvas.stateManager.getPortColor(this._portB.portData);
            const splitComponents = extractedColorB.split("_").map((v) => parseInt(v));
            if (splitComponents[0] > 0 && splitComponents[1] === 0 && splitComponents[2] === 0) {
                extractedColorB = "";
            }
        }
        if (extractedColorB && extractedColorA !== extractedColorB) {
            // Gradient
            const svg = this._graphCanvas.svgCanvas;
            const defs = svg.querySelector("defs") || svg.appendChild(document.createElementNS("http://www.w3.org/2000/svg", "defs"));
            if (!this._gradient) {
                this._gradient = document.createElementNS("http://www.w3.org/2000/svg", "linearGradient");
                defs.appendChild(this._gradient);
                const stop1 = document.createElementNS("http://www.w3.org/2000/svg", "stop");
                this._gradient.appendChild(stop1);
                const stop2 = document.createElementNS("http://www.w3.org/2000/svg", "stop");
                this._gradient.appendChild(stop2);
            }
            const stop1 = this._gradient.children[0];
            const stop2 = this._gradient.children[1];
            const gradientId = `linkGradient_${extractedColorA}_${extractedColorB}`;
            this._gradient.id = gradientId;
            this._gradient.setAttribute("x1", startX < endX ? "0" : "1");
            this._gradient.setAttribute("y1", "0");
            this._gradient.setAttribute("x2", startX < endX ? "1" : "0");
            this._gradient.setAttribute("y2", "0");
            stop1.setAttribute("offset", "0%");
            stop1.setAttribute("stop-color", extractedColorA);
            stop2.setAttribute("offset", "100%");
            stop2.setAttribute("stop-color", extractedColorB);
            this._path.setAttribute("stroke", `url(#${gradientId})`);
        }
        else {
            this._path.setAttribute("stroke", extractedColorA);
        }
    }
    get path() {
        return this._path;
    }
    get selectionPath() {
        return this._selectionPath;
    }
    constructor(graphCanvas, portA, nodeA, portB, nodeB) {
        this._isVisible = true;
        this._isTargetCandidate = false;
        this._flowAnimationActive = false;
        this.onDisposedObservable = new Observable();
        this._portA = portA;
        this._portB = portB;
        this._nodeA = nodeA;
        this._nodeB = nodeB;
        this._graphCanvas = graphCanvas;
        const document = portA.element.ownerDocument;
        const svg = graphCanvas.svgCanvas;
        // Create path
        this._path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        this._path.setAttribute("fill", "none");
        this._path.classList.add(styles["link"]);
        svg.appendChild(this._path);
        this._selectionPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
        this._selectionPath.setAttribute("fill", "none");
        this._selectionPath.classList.add(styles["selection-link"]);
        svg.appendChild(this._selectionPath);
        this._selectionPath.onmousedown = (evt) => this.onClick(evt);
        if (this._portB) {
            // Update
            this.update();
        }
        this._onSelectionChangedObserver = this._graphCanvas.stateManager.onSelectionChangedObservable.add((options) => {
            const { selection } = options || {};
            if (selection === this) {
                this._path.classList.add(styles["selected"]);
                this._selectionPath.classList.add(styles["selected"]);
            }
            else {
                this._path.classList.remove(styles["selected"]);
                this._selectionPath.classList.remove(styles["selected"]);
            }
        });
    }
    onClick(evt) {
        const stateManager = this._graphCanvas.stateManager;
        const nodeA = this._nodeA;
        const pointA = this._portA.portData;
        const nodeB = this._nodeB;
        const pointB = this._portB.portData;
        const reconnect = (newNode) => {
            const newBlock = newNode.content.data;
            // Delete previous link
            this.dispose();
            // Connect to the new block
            this._graphCanvas.connectNodes(nodeA, pointA, newNode, newNode.getPortDataForPortDataContent(newBlock.input));
            this._graphCanvas.connectNodes(newNode, newNode.getPortDataForPortDataContent(newBlock.output), nodeB, pointB);
            stateManager.onRebuildRequiredObservable.notifyObservers();
        };
        if (evt.altKey) {
            if (!stateManager.isElbowConnectionAllowed(this._portA, this._portB)) {
                return;
            }
            // Create an elbow at the clicked location
            stateManager.onNewNodeCreatedObservable.addOnce(reconnect);
            stateManager.onNewBlockRequiredObservable.notifyObservers({
                type: "ElbowBlock",
                targetX: evt.clientX,
                targetY: evt.clientY,
                needRepositioning: true,
            });
            // Make sure the undo/redo stack is reverted as we did 2 actions (create and connect)
            stateManager.historyStack.collapseLastTwo();
            return;
        }
        if (evt.ctrlKey) {
            if (!stateManager.isDebugConnectionAllowed(this._portA, this._portB)) {
                return;
            }
            // Create a debug at the clicked location
            stateManager.onNewNodeCreatedObservable.addOnce(reconnect);
            stateManager.onNewBlockRequiredObservable.notifyObservers({
                type: "DebugBlock",
                targetX: evt.clientX,
                targetY: evt.clientY,
                needRepositioning: true,
            });
            // Make sure the undo/redo stack is reverted as we did 2 actions (create and connect)
            stateManager.historyStack.collapseLastTwo();
            return;
        }
        stateManager.onSelectionChangedObservable.notifyObservers({ selection: this });
    }
    /** Ensure the shared SVG glow filter exists, return its id
     * @param svg the SVG element to check for the filter and add it to if not present
     * @returns the id of the glow filter to use in this SVG
     */
    static _EnsureGlowFilter(svg) {
        const filterId = "flowDotGlow";
        if (!svg.querySelector(`#${filterId}`)) {
            const ns = "http://www.w3.org/2000/svg";
            const doc = svg.ownerDocument;
            let defs = svg.querySelector("defs");
            if (!defs) {
                defs = doc.createElementNS(ns, "defs");
                svg.prepend(defs);
            }
            const filter = doc.createElementNS(ns, "filter");
            filter.setAttribute("id", filterId);
            filter.setAttribute("x", "-50%");
            filter.setAttribute("y", "-50%");
            filter.setAttribute("width", "200%");
            filter.setAttribute("height", "200%");
            const blur = doc.createElementNS(ns, "feGaussianBlur");
            blur.setAttribute("stdDeviation", "3");
            blur.setAttribute("result", "blur");
            const merge = doc.createElementNS(ns, "feMerge");
            const n1 = doc.createElementNS(ns, "feMergeNode");
            n1.setAttribute("in", "blur");
            const n2 = doc.createElementNS(ns, "feMergeNode");
            n2.setAttribute("in", "SourceGraphic");
            merge.appendChild(n1);
            merge.appendChild(n2);
            filter.appendChild(blur);
            filter.appendChild(merge);
            defs.appendChild(filter);
        }
        return filterId;
    }
    /**
     * Triggers a brief animated dot traveling along the link path from port A to port B.
     * @param durationMs how long the animation takes (default 600ms)
     * @param color the color of the dot (default green)
     */
    triggerFlowAnimation(durationMs = 600, color = "#33B766") {
        if (this._flowAnimationActive || !this._path.parentElement) {
            return;
        }
        const totalLength = this._path.getTotalLength();
        if (totalLength === 0) {
            return;
        }
        this._flowAnimationActive = true;
        const svg = this._path.parentElement;
        const ns = "http://www.w3.org/2000/svg";
        const doc = this._path.ownerDocument;
        const filterId = NodeLink._EnsureGlowFilter(svg);
        const dot = doc.createElementNS(ns, "circle");
        dot.setAttribute("r", "6");
        dot.setAttribute("fill", color);
        dot.setAttribute("filter", `url(#${filterId})`);
        dot.style.pointerEvents = "none";
        svg.appendChild(dot);
        const startTime = performance.now();
        const animate = (now) => {
            const t = Math.min((now - startTime) / durationMs, 1);
            const pt = this._path.getPointAtLength(t * totalLength);
            dot.setAttribute("cx", String(pt.x));
            dot.setAttribute("cy", String(pt.y));
            if (t < 1) {
                requestAnimationFrame(animate);
            }
            else {
                dot.remove();
                this._flowAnimationActive = false;
            }
        };
        requestAnimationFrame(animate);
    }
    /**
     * Disposes this visual link.
     * @param notify - Whether to notify observers that the link was disposed.
     * @param disconnectPorts - Whether to disconnect the underlying port data.
     */
    dispose(notify = true, disconnectPorts = true) {
        this._graphCanvas.stateManager.onSelectionChangedObservable.remove(this._onSelectionChangedObserver);
        if (this._path.parentElement) {
            this._path.parentElement.removeChild(this._path);
        }
        if (this._selectionPath.parentElement) {
            this._selectionPath.parentElement.removeChild(this._selectionPath);
        }
        if (this._gradient) {
            if (this._gradient.parentElement) {
                this._gradient.parentElement.removeChild(this._gradient);
            }
        }
        if (this._nodeB) {
            this._nodeA.links.splice(this._nodeA.links.indexOf(this), 1);
            this._nodeB.links.splice(this._nodeB.links.indexOf(this), 1);
            this._graphCanvas.links.splice(this._graphCanvas.links.indexOf(this), 1);
            if (disconnectPorts) {
                this._portA.portData.disconnectFrom(this._portB.portData);
            }
            RefreshNode(this._nodeB, undefined, undefined, this._graphCanvas);
        }
        if (notify) {
            this.onDisposedObservable.notifyObservers(this);
            this.onDisposedObservable.clear();
        }
    }
}
//# sourceMappingURL=nodeLink.js.map